import React, { Component } from 'react';

import * as MicrosoftGraph from "@microsoft/microsoft-graph-client";
import { IPersonaProps, Label, NormalPeoplePicker, PeoplePickerItemSuggestion } from "office-ui-fabric-react";
import { connect } from "react-redux";
import { IRootState } from "../store";
import ApiService from "./ApiService";
import { Dictionary } from 'lodash';

export interface UserOption {
    id: string;
    userPrincipalName: string;
    displayName: string;
}

interface UserSelectorBaseProps {
    className: string;
    label?: string;
    selectedUsers?: (UserOption | string)[];
    onSelectionChanged: (selectedUsers: UserOption[]) => void;
    disabled: boolean;
    multiSelect: boolean;
    textExtraDetails: string;

    fetchUsers: (filter: string) => Promise<UserOption[]>;

    graphClient: MicrosoftGraph.Client;
    apiService: ApiService;
}

interface UserSelectorBaseState {
    userCache: Dictionary<UserOption>;
}

class UserSelectorBase extends Component<UserSelectorBaseProps, UserSelectorBaseState> {
    static displayName = UserSelectorBase.name;

    static userOptionToPersonaProps(user: UserOption): IPersonaProps {
        return {
            id: user.id,
            text: user.displayName,
            secondaryText: `${user.userPrincipalName} ${user.id}`,
            tertiaryText: user.userPrincipalName,
        }
    }

    constructor(props: UserSelectorBaseProps) {
        super(props);

        this.state = {
            userCache: {}
        };
    }

    componentDidMount(): void {
        if (this.props.selectedUsers) {
            this.ensureSelectedUsersAreCached();
        }
    }


    componentDidUpdate(prevProps: Readonly<UserSelectorBaseProps>, prevState: Readonly<UserSelectorBaseState>, snapshot?: any): void {
        if (this.props.selectedUsers && this.props.selectedUsers !== prevProps.selectedUsers) {
            this.ensureSelectedUsersAreCached();
        }
    }

    private ensureSelectedUsersAreCached()
    {
        if(!this.props.selectedUsers)
        {
            return;
        }
        
        this.props.selectedUsers.forEach((selected: string | UserOption) => {
            if (typeof selected !== 'string') {
                this.cacheUsers([selected])
                return;
            }
            
            const userId = selected as string;
            if (this.state.userCache[userId] !== undefined) {
                //user already loaded in cache
                return;
            }

            // TODO: could get multiple users in one request
            this.props.fetchUsers(userId).then((loadedUsers) => {
                this.cacheUsers(loadedUsers)
            });
        });

    }
    
    private cacheUsers(users: UserOption[])
    {
        users.forEach(user => {
            this.state.userCache[user.id] = user;
        })
        const newCache = Object.assign({}, this.state.userCache)
        this.setState({userCache: newCache});
    }

    private onPeopleChange = (selectedPersonaProps?: IPersonaProps[]) => {

        if (selectedPersonaProps === undefined) {
            return;
        }
        
        const selectedUserOptions: UserOption[] = selectedPersonaProps!.map(user => this.state.userCache[user.id!]);
        const selectedUsers = this.props.multiSelect ? selectedUserOptions : selectedUserOptions!.slice(0, 1);

        this.props.onSelectionChanged(selectedUsers);
    }

    private renderLabel() {
        if (this.props.label !== undefined) {
            return (
                <Label>{this.props.label}</Label>
            );
        }
    }

    private loadSuggestions = async (filter: string): Promise<IPersonaProps[]> => {
        const users = await this.props.fetchUsers(filter);
        this.cacheUsers(users);
        return users.map(UserSelectorBase.userOptionToPersonaProps)
    }

    render(): JSX.Element {

        const personaPropsForSelectedUsers = this.props.selectedUsers?.map(selectedOption => {

            if (typeof selectedOption === "string")
            {
                const user = this.state.userCache[selectedOption];
                if (user === undefined)
                {
                    return {
                        id: selectedOption,
                        text: "loading...",
                        secondaryText: "loading...",
                    };
                }
                else
                {
                    return UserSelectorBase.userOptionToPersonaProps(user);
                }
            }
            else
            {
                const user = this.state.userCache[selectedOption.id] || selectedOption;
                return UserSelectorBase.userOptionToPersonaProps(user);
            }
        });

        const onRenderSuggestionsItem = (personaProps: any, suggestionsProps: any) => (
            <PeoplePickerItemSuggestion
                personaProps={personaProps}
                suggestionsProps={suggestionsProps}
                styles={{ personaWrapper: { width: '100%' } }
                } />
        );

        return (
            <div className={this.props.className}>
                {this.renderLabel()}
                <NormalPeoplePicker
                    disabled={this.props.disabled}
                    onResolveSuggestions={this.loadSuggestions}//somethingSuggestions
                    selectedItems={personaPropsForSelectedUsers}
                    onChange={this.onPeopleChange}
                    className={'ms-PeoplePicker'}
                    key={'normal'}
                    removeButtonAriaLabel={'Remove'}
                    inputProps={{
                        onBlur: (ev: React.FocusEvent<HTMLInputElement>) => console.log('onBlur called'),
                        onFocus: (ev: React.FocusEvent<HTMLInputElement>) => console.log('onFocus called'),
                        'aria-label': 'People Picker'
                    }}
                    onRenderSuggestionsItem={onRenderSuggestionsItem}
                    resolveDelay={300}
                />
                <div className={"text-extra-detail"}>{this.props.textExtraDetails}</div>
            </div>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: { className: string, label?: string, selectedUsers?: (UserOption | string)[], onSelectionChanged(selectedUsers: UserOption[]): void, disabled: boolean, multiSelect: boolean, textExtraDetails: string, fetchUsers: (filter: string) => Promise<UserOption[]> }): UserSelectorBaseProps => {
        return {
            className: ownProps.className,
            label: ownProps.label,
            selectedUsers: ownProps.selectedUsers,
            onSelectionChanged: ownProps.onSelectionChanged,
            disabled: ownProps.disabled,
            multiSelect: ownProps.multiSelect,
            textExtraDetails: ownProps.textExtraDetails,
            fetchUsers: ownProps.fetchUsers,

            graphClient: state.auth.graphClient,
            apiService: state.auth.apiService,
        };
    }
)(UserSelectorBase)